/**
 * Outbound-messages window for AG-UI runs.
 *
 * CopilotKit re-sends the full `messages[]` on every turn — including
 * embedded tool result content, which for some of our tools (skill_load,
 * manage_domain read_shared, file_read on domain docs) is 10-30 KB per
 * entry. After a rich first turn the accumulated payload for turn 2 can
 * exceed AgentCore's 64 KB per-frame WebSocket limit; the proxy closes
 * with code 1009 before the run even reaches the Runtime, and Gateway
 * surfaces this to the client as `RUN_ERROR code=PAYLOAD_TOO_LARGE`.
 *
 * The Runtime keeps authoritative history in AgentCore memory, so the
 * wire only needs to carry the newest user turn — the agent reaches
 * prior context through its own memory tools.
 *
 * HITL exception: when a frontend tool (e.g. approve_audience_segment)
 * resolves, `@copilotkit/core@processAgentResult` inserts a `role: 'tool'`
 * message with the handler result and immediately calls `runAgent()` to
 * feed that result back to the runtime. If we trim those tool messages,
 * the resume payload is dropped and the runtime never sees the human's
 * answer — the run stalls or rewinds one turn.
 *
 * ⚠️ "Keep everything after the latest user" is NOT enough for that resume,
 * and the difference is a hard failure, not a slow path. The approval gate
 * fires at the END of the turn that produced the preview, so everything that
 * turn read sits after the latest user message: four `manage_domain` file
 * reads plus seven `direct_query` results, each carrying an enriched sample
 * table. Nothing gets trimmed, the frame blows the limit, and the relay drops
 * it — measured on beta 2026-09-23, three clicks on 圈人 approval, three
 * `AG-UI relay hit WS frame size limit (rcvd=1009 sent=1009)` in
 * gateway-server, zero Runtime log lines. The user sees the card vanish and
 * nothing happen: the frontend already resolved the Promise, so there is no
 * retry and no error surfaced anywhere in the UI.
 *
 * So a resume gets its own, much narrower window: the tool results being fed
 * back, the assistant message that owns those `toolCall`s, and the latest
 * user message. That pairing is the whole of what the runtime needs to unblock
 * the paused run — the preview it is resuming into comes from AgentCore STM
 * rehydration, not from this payload.
 *
 * NOTE (per ag-ui docs): `AbstractAgent.connectAgent()` bypasses
 * middleware; only `runAgent()` runs it. `AgUiChatPanel` uses
 * `copilotkit.runAgent(...)` — this middleware will fire on every
 * user-initiated turn.
 */

import type { MiddlewareFunction } from '@ag-ui/client';

type AguiMessage = {
  role?: string;
  toolCallId?: unknown;
  toolCalls?: unknown;
  [k: string]: unknown;
};

function latestUserIndex(messages: readonly AguiMessage[]): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === 'user') { return i; }
  }
  return -1;
}

/** `{id, name}` for each toolCall on an assistant message; name may be absent. */
function toolCallsOf(message: AguiMessage): { id?: string; name?: string }[] {
  const calls = message.toolCalls;
  if (!Array.isArray(calls)) { return []; }
  return calls.map((c) => {
    const call = c as { id?: unknown; function?: { name?: unknown }; name?: unknown };
    const raw = call.function?.name ?? call.name;
    return {
      id: typeof call.id === 'string' ? call.id : undefined,
      name: typeof raw === 'string' ? raw : undefined,
    };
  });
}

/**
 * Indices that a HITL resume needs kept, or `null` when there is no fed-back
 * tool result to carry.
 *
 * A resume is identified by WHICH TOOL produced the result, not by where the result
 * sits. Two earlier heuristics were both wrong:
 *   - "the trailing run of role:'tool' messages" — when the operator types instead of
 *     clicking, the panel appends their `role: 'user'` row while CopilotKit splices the
 *     tool result next to its owning assistant message, i.e. BEFORE that row. The tail
 *     is then a user message, the payload stops looking like a resume, and the trim
 *     drops the very result the runtime is waiting for — producing an agent that had no
 *     idea its gate was dismissed and told the operator to click a card already gone.
 *   - "any role:'tool' message" — that also matches the BACKEND tool results of the same
 *     turn (domain file reads, query results), which is exactly the tens of KB this
 *     window exists to drop.
 * The discriminating property is that the tool executes in the browser, and the run
 * input already carries that list (`RunAgentInput.tools`), so nothing is hardcoded here.
 *
 * We keep the results being fed back, the assistant message(s) declaring their
 * `toolCall`s, and every other result belonging to those same assistant messages
 * (an assistant whose `toolCalls` are not all resolved in the window is as invalid
 * as an orphan tool message, and Strands can batch several calls into one message).
 *
 * Returns `null` — i.e. contributes nothing — whenever the pairing cannot be
 * completed. Sending too much only risks the size limit; sending an unpaired
 * `toolCall`/`toolCallId` is rejected outright.
 */
function hitlResumeIndices(
  messages: readonly AguiMessage[],
  frontendToolNames: ReadonlySet<string>,
): Set<number> | null {
  if (frontendToolNames.size === 0) { return null; }

  // toolCallIds declared by an assistant message for a FRONTEND tool.
  const frontendCallIds = new Set<string>();
  messages.forEach((m) => {
    if (m.role !== 'assistant') { return; }
    toolCallsOf(m).forEach((c) => {
      if (typeof c.id === 'string' && c.name && frontendToolNames.has(c.name)) {
        frontendCallIds.add(c.id);
      }
    });
  });

  // 🔴 Exactly ONE gate pair, and only while it is still unanswered. Both halves are
  // load-bearing; either alone leaves the window growing.
  //
  // A settled frontend-tool result is never removed from `agent.messages` — CopilotKit
  // splices it in and `prepareRunAgentInput` ships a full `structuredClone` every run. So
  // "does a frontend result exist anywhere in the history" is true for the rest of the
  // session once a card has been answered, and this function used to answer yes forever:
  // the window grew by a pair per gate (measured 3 → 5 → 7), every later turn re-sent a
  // completed `{approved:true}` as the newest context, and `resumeResultKept` reported
  // `true` on turns with no resume at all — destroying the one signal that tells a dropped
  // HITL result from a healthy turn. Worst case is a frame-limit regression in the file
  // that exists to prevent it: the sibling-result rule below deliberately keeps every
  // result of a kept assistant, so a gate batched with a 30 KB `direct_query` re-sent that
  // 30 KB on every remaining turn (measured: >30 000 bytes for a 6-message history).
  //
  // ⚠️ This is scoping the old positional heuristic used to get for free — it keyed on the
  // TRAILING run of `role:'tool'` messages, so a history ending in anything else could not
  // look like a resume. Keying on tool identity instead was right, but dropped that.
  let newestIdx = -1;
  let newestId: string | undefined;
  messages.forEach((m, i) => {
    const id = m.toolCallId;
    if (m.role === 'tool' && typeof id === 'string' && frontendCallIds.has(id)) {
      newestIdx = i;
      newestId = id;
    }
  });
  if (newestId === undefined) { return null; }

  // Consumed? An assistant message after the result means the runtime already acted on it
  // and this turn is ordinary. A trailing USER message must NOT disqualify it — that is the
  // shape typing past a card produces, and dropping the result there is the exact failure
  // the comment above `hitlResumeIndices` records.
  for (let i = newestIdx + 1; i < messages.length; i++) {
    if (messages[i].role === 'assistant') { return null; }
  }

  const keep = new Set<number>();
  const owned = new Set<string>();
  messages.forEach((m, i) => {
    if (m.role !== 'assistant') { return; }
    const ids = toolCallsOf(m).map((c) => c.id).filter((i): i is string => !!i);
    if (!ids.includes(newestId as string)) { return; }
    keep.add(i);
    ids.forEach((id) => owned.add(id));
  });
  if (keep.size === 0) { return null; }

  const resolved = new Set<string>();
  messages.forEach((m, i) => {
    const id = m.toolCallId;
    if (m.role !== 'tool' || typeof id !== 'string' || !owned.has(id)) { return; }
    keep.add(i);
    resolved.add(id);
  });
  for (const id of owned) {
    if (!resolved.has(id)) { return null; }
  }
  return keep;
}

/**
 * Does this payload carry an unanswered frontend-tool result — i.e. is it a HITL resume?
 *
 * Exported so the log line can say something true. `trimmed.some(m => m.role === 'tool')`
 * was not it: the fallback window keeps BACKEND results (domain reads, query rows) too, so
 * that flag read `true` on plenty of turns carrying no resume at all — and it is the only
 * external evidence of whether a resume result survived, so a false positive there is worse
 * than no log.
 */
export function isHitlResume(
  messages: readonly AguiMessage[],
  frontendToolNames: ReadonlySet<string> = new Set(),
): boolean {
  return hitlResumeIndices(messages, frontendToolNames) !== null;
}

export function trimMessages(
  messages: readonly AguiMessage[],
  frontendToolNames: ReadonlySet<string> = new Set(),
): AguiMessage[] {
  const resume = hitlResumeIndices(messages, frontendToolNames);
  const lastUserIdx = latestUserIndex(messages);

  // Union, not either/or. A turn can be BOTH a resume and a fresh user message —
  // that is exactly what typing while an approval card is up produces — and the
  // runtime needs both halves: the tool result to unblock the paused run, the user
  // message to know what they actually asked for.
  if (resume) {
    const keep = new Set<number>(resume);
    if (lastUserIdx !== -1) { keep.add(lastUserIdx); }
    return messages.filter((_, i) => keep.has(i));
  }

  if (lastUserIdx === -1) {
    return [...messages];
  }
  return messages.slice(lastUserIdx);
}

export const messageWindowMiddleware: MiddlewareFunction = (input, next) => {
  const original = input.messages as unknown as AguiMessage[];
  // The frontend-tool list rides on every RunAgentInput, so the middleware can tell a
  // browser-executed result (the resume payload) from a backend one without knowing
  // any tool name itself.
  const frontendToolNames = new Set(
    ((input.tools ?? []) as { name?: unknown }[])
      .map((t) => t?.name)
      .filter((n): n is string => typeof n === 'string'),
  );
  const trimmed = trimMessages(original, frontendToolNames);
  // console.info, not debug: `debug` lands in DevTools' Verbose level, which is hidden
  // by default, and this line is the only externally visible evidence of what the
  // resume payload ended up being. Logged unconditionally — "nothing was trimmed" is
  // just as diagnostic as a trim, and one line per turn is a fair price for being able
  // to tell a dropped HITL result from a non-compliant agent without a debugger.
  const resumeKept = isHitlResume(original, frontendToolNames);
  // eslint-disable-next-line no-console
  console.info(
    '[agui/messageWindow] %d → %d msgs | resumeResultKept=%s | frontendTools=%s',
    original.length,
    trimmed.length,
    resumeKept,
    [...frontendToolNames].join(',') || '(none)',
  );
  return next.run({
    ...input,
    messages: trimmed as unknown as typeof input.messages,
  });
};
